// @ts-check
/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from 'theme-ui';
import BaseExhibitorCard from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorsList/ExhibitorCard';
import { graphql as gatsbyGraphQL } from 'gatsby';
import Image from 'gatsby-image';
import { IoImagesOutline } from 'react-icons/io5';
import React from 'react';
import { getFluidImage } from '@bottlebooks/gatsby-theme-event/src/helpers/getImageProps';
import {
  Box,
  Flex,
  Link,
  Text,
  VideoIcon,
  ShoppingCartIcon,
} from '@bottlebooks/gatsby-theme-base';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
// eslint-disable-next-line import/no-unresolved
import Card from '@bottlebooks/gatsby-design-system/src/components/Card/Card';

export * from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorsList/ExhibitorCard';

export default function ExhibitorCard({ exhibitor: data, ...rest }) {
  const exhibitor = afterQuery(data);
  switch (exhibitor.exhibitorPackage?.key) {
    case 'premium':
      return <PremiumExhibitorCard exhibitor={exhibitor} {...rest} />;

    case 'pavilion':
      return <PavilionCard exhibitor={exhibitor} {...rest} />;

    default:
      return <StandardExhibitorCard exhibitor={exhibitor} {...rest} />;
  }
}

/**
 *
 * @param {{
 *  exhibitor: object;
 *  children?: React.ReactNode;
 *  to?: string;
 *  onClick?: () => void;
 *  [x: string]: any;
 * }} props
 * @returns
 */
function StandardExhibitorCard({ exhibitor, children, to, onClick, ...rest }) {
  return (
    <Card {...rest}>
      <BaseExhibitorCard.Link exhibitor={exhibitor} to={to} onClick={onClick}>
        <BaseExhibitorCard.Logo gap={0} exhibitor={exhibitor}>
          <CertificationLogos exhibitor={exhibitor} sx={{ marginTop: -1 }}>
            {children}
          </CertificationLogos>
        </BaseExhibitorCard.Logo>
        <BaseExhibitorCard.Main
          sx={{
            paddingTop: 2,
            backgroundColor: 'light',
            borderTop: 1,
            borderColor: 'border',
          }}
          exhibitor={exhibitor}
        >
          <BaseExhibitorCard.Exhibitor exhibitor={exhibitor} />
        </BaseExhibitorCard.Main>
      </BaseExhibitorCard.Link>
      <BaseExhibitorCard.Bookmark exhibitor={exhibitor} />
    </Card>
  );
}

/**
 *
 * @param {{ exhibitor: object; elevation?: Parameters<Card>[0]["elevation"]; to?: string; onClick?: () => void; className?: string; [x: string]: any }} props
 * @returns
 */
function PremiumExhibitorCard({
  exhibitor,
  exhibitorPackageText = 'Premiumeintrag',
  elevation,
  to,
  onClick,
  className,
  image = exhibitor.profile?.mainImage?.fluid,
  ...rest
}) {
  const { i18n } = useLingui();
  const hasImage = image;
  return (
    <Card
      elevation={elevation}
      sx={{
        gridColumn: [null, null, 'span 2'],
        gridRow: 'span 2',
        gridAutoRows: '1fr 0 1fr',
        '.bb-hoverimg': {
          transition: 'transform',
          transformOrigin: 'top center',
        },
        ':hover .bb-hoverimg': { transform: 'scale(1.02)' },
      }}
      className={className}
    >
      <Link
        to={to}
        onClick={onClick}
        sx={{
          display: 'grid',
          overflow: 'hidden',
          margin: 3,
          marginBottom: -3,
        }}
      >
        {hasImage ? (
          <Image className="bb-hoverimg" fluid={getFluidImage(image)} />
        ) : (
          <Box
            className="bb-hoverimg"
            sx={{
              backgroundColor: 'nuanced',
              backgroundImage:
                'repeating-linear-gradient(35deg, white, transparent 32px )',
            }}
          />
        )}
      </Link>
      <Flex aria-hidden sx={{ zIndex: 'elevated', paddingX: 3 }}>
        <Box
          sx={{
            borderBottom: '16px solid',
            borderBottomColor: 'background',
            borderRight: '16px solid',
            borderRightColor: 'transparent',
            flex: 1,
          }}
        >
          <Text variant="smallest" sx={{ color: 'lightText', paddingTop: 0.5 }}>
            {exhibitorPackageText}
          </Text>
        </Box>
        <Box
          sx={{
            borderBottom: '16px solid',
            borderBottomColor: 'background',
            borderLeft: '16px solid',
            borderLeftColor: 'transparent',
            flex: 1,
          }}
        ></Box>
      </Flex>

      <StandardExhibitorCard
        exhibitor={exhibitor}
        to={to}
        onClick={onClick}
        sx={{ marginTop: 3 }}
        {...rest}
        elevation="none"
      >
        {exhibitor.offeringTypes?.includes('onlineShop') && (
          <ShoppingCartIcon size="xsmall" title={t`Online shop`} />
        )}
        {exhibitor.offeringTypes?.includes('videos') && (
          <VideoIcon size="xsmall" title={i18n._(t`Videos`)} />
        )}
        {exhibitor.offeringTypes?.includes('imageGallery') && (
          <IoImagesOutline title={i18n._(t`Image gallery`)} />
        )}
      </StandardExhibitorCard>
    </Card>
  );
}

function CertificationLogos({ exhibitor, children = undefined, ...rest }) {
  return (
    <Flex
      gap={3}
      justify="flex-end"
      align="center"
      sx={{ color: 'primary' }}
      {...rest}
    >
      {exhibitor.zertifizierungslogos?.map(({ key, value }) => (
        <Image
          key={key}
          alt={value}
          title={value}
          fixed={{
            src: `https://res.cloudinary.com/bottlebooks/image/upload/w_96,h_96,c_pad,f_auto/vievinum/${key}.png`,
            srcSet: '',
            width: 24,
            height: 24,
          }}
        />
      ))}
      {children}
    </Flex>
  );
}

function PavilionCard({ exhibitor, ...rest }) {
  return (
    <PremiumExhibitorCard
      exhibitor={exhibitor}
      exhibitorPackageText="Premium-Pavillon"
      image={getFluidImage(exhibitor.profile?.mainImage?.fluid, {
        width: 1920,
        height: 1200,
      })}
      {...rest}
      sx={{
        gridColumn: ['1/-1', null, '1/-1'],
        gridAutoRows: ['1fr 0 1fr', null, null, '2fr 0 1fr'],
        '.bb-hoverimg > div[aria-hidden]': { display: 'none' },
      }}
    />
  );
}

export const fragment = gatsbyGraphQL/* GraphQL */ `
  fragment vv_exhibitorPackage on Bottlebooks_SingleRegistration {
    exhibitorPackage: customFieldValue(key: "exhibitorPackage") {
      ... on Bottlebooks_SelectFieldValue {
        key
      }
    }
  }
  fragment vv_ExhibitorCard_Registration on Bottlebooks_SingleRegistration {
    registrationId
    ...vv_exhibitorPackage
    zertifizierungslogos: customFieldValue(key: "zertifizierungslogos") {
      ... on Bottlebooks_MultiSelectFieldValue {
        values {
          key
          value
        }
      }
    }
    profile {
      mainImage {
        fluid(maxWidth: 700, maxHeight: 350, crop: FILL, gravity: FACES_AUTO) {
          src
          sizes
          aspectRatio
        }
      }
    }
  }
`;

function afterQuery(exhibitor) {
  return {
    ...exhibitor,
    exhibitorPackage: exhibitor?.exhibitorPackage,
    zertifizierungslogos: exhibitor?.zertifizierungslogos?.values,
  };
}
